import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import api, { formatINR, SQM_TO_SQFT } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, Trash2, ArrowLeft, FileText, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import DrawingPreview from '../components/DrawingPreview';

const MESH_OPTIONS = ['None', 'Fiber Mesh', 'SS Mesh'];
const HANDLE_CHOICES = ['None', 'Push Lock', 'Flush Lock', 'Patio Handle', 'D-Handle', 'C Handle', 'Touch Lock + Rollers', 'Not Applicable'];

const DEFAULT_TERMS = `1. Payment Terms:\n   a. 90% advance along with order, 10% after installation if less than Rs.1,00,000/-\n   b. 50% advance along with order, 40% before delivery, 10% after installation if more than Rs.1,00,000/-\n2. Validation of quote 25 days. Total execution of project should be completed latest by 3 months.\n3. Prices are based on sizes provided by customer. Valid for variation up to +/- 50mm per window provided design remains unchanged.\n4. Window security tape removal after installation: Rs.100/- per window.\n5. If any other commitments given by sales team, please call before placing order.\n6. Post-handover service: Rs.350/- per visit.\n7. Material unloading & storage is customer's scope.\n8. All disputes subject to Chapra jurisdiction only.\n\nPre-Requisites for Installation:\n1. Walls plastered inside/outside, POP complete.\n2. All jams, sills and soffits plastered.\n3. Flooring complete where doors installed.\n4. Aperture should be smooth.\n5. Base/top water levelled, sides in vertical plumb.\n6. Sill width > window width.\n7. Opening accessible from inside.\n8. Install before last coat of paint.\n9. Scaffoldings should not block window openings.`;

export default function CreateQuotationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [designTypes, setDesignTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [status, setStatus] = useState('Draft');

  const [form, setForm] = useState({
    customer_id: '', customer_name: '', customer_phone: '', customer_email: '',
    customer_address: '', delivery_address: '', gst_type: 'CGST_SGST', gst_rate: 18,
    transport_charges: 0, installation_charges: 0, terms: DEFAULT_TERMS,
  });
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({
    vendor_id: '', design_type: '', width_mm: '', height_mm: '', qty: 1,
    glass_type: '', color: 'White', net_type: 'None', handle_inner: '', handle_outer: '', handle_mesh: '',
    hinge_type: '', frame: '', sash: '', series: '', rate_per_sqft: '',
    template_code: null, _templateOriginal: null,
  });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/customers').then(r => setCustomers(r.data)),
      api.get('/vendors').then(r => setVendors(r.data)),
      api.get('/design-types').then(r => setDesignTypes(r.data)),
      api.get('/product-templates').then(r => setTemplates(r.data)),
    ]).catch(() => {});
  }, []);

  // Load existing quotation for edit mode
  useEffect(() => {
    if (!isEdit || !editId) return;
    (async () => {
      try {
        const { data } = await api.get(`/quotations/${editId}`);
        setForm({
          customer_id: data.customer_id || '',
          customer_name: data.customer_name || '',
          customer_phone: data.customer_phone || '',
          customer_email: data.customer_email || '',
          customer_address: data.customer_address || '',
          delivery_address: data.delivery_address || '',
          gst_type: data.gst_type || 'CGST_SGST',
          gst_rate: data.gst_rate ?? 18,
          transport_charges: data.transport_charges ?? 0,
          installation_charges: data.installation_charges ?? 0,
          terms: data.terms || DEFAULT_TERMS,
        });
        setItems((data.items || []).map((it, i) => ({ ...it, sno: i + 1 })));
        setQuoteNumber(data.quote_number || '');
        setStatus(data.status || 'Draft');
      } catch {
        toast.error('Could not load quotation');
        navigate('/quotations');
      } finally {
        setLoadingExisting(false);
      }
    })();
  }, [editId, isEdit, navigate]);

  // Auto-select template from URL param
  useEffect(() => {
    const tplCode = searchParams.get('template');
    if (tplCode && templates.length > 0) {
      const tpl = templates.find(t => t.code === tplCode);
      if (tpl) applyTemplate(tpl);
    }
  }, [searchParams, templates]);

  // Deep-link from Design Catalog: prefill design_type
  useEffect(() => {
    const designName = searchParams.get('design');
    if (designName) {
      setNewItem(prev => ({ ...prev, design_type: designName }));
      setShowDetails(true);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="new-item-form"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, [searchParams]);

  // Auto-calculate installation: ₹30/sqft, min ₹1800
  // Use area_sqm * 10.764 for precision (area_sqft is stored rounded to 2 decimals)
  // Skip during edit-mode initial load so we don't overwrite the user's saved value.
  useEffect(() => {
    if (loadingExisting) return;
    const totalSqft = items.reduce((s, i) => {
      // Prefer area_sqm * 10.764 (more precise) if area_sqm > 0
      const sqft = i.area_sqm ? (i.area_sqm * 10.764) : (i.area_sqft || 0);
      return s + (sqft * (i.qty || 1));
    }, 0);
    const calc = Math.round(totalSqft * 30);
    const installCharge = Math.max(calc, items.length > 0 ? 1800 : 0);
    setForm(prev => ({ ...prev, installation_charges: installCharge }));
  }, [items, loadingExisting]);

  const applyTemplate = (tpl) => {
    const original = { frame: tpl.frame, sash: tpl.sash, glass: tpl.glass, hardware: tpl.hardware, hinge: tpl.hinge, net: tpl.net, rate_per_sqft: tpl.rate_per_sqft };
    setNewItem(prev => ({
      ...prev,
      design_type: tpl.name,
      frame: tpl.frame || '', sash: tpl.sash || '',
      glass_type: tpl.glass || '', hinge_type: tpl.hinge || '',
      net_type: tpl.net || 'None', series: 'PR_60_Series',
      rate_per_sqft: tpl.rate_per_sqft || '',
      template_code: tpl.code,
      _templateOriginal: original,
      // W & H always stay empty
      width_mm: '', height_mm: '',
    }));
    setShowDetails(true);
    toast.success(`Template "${tpl.code} - ${tpl.name}" applied. Enter Width & Height.`);
  };

  const resetField = (field) => {
    if (!newItem._templateOriginal) return;
    const fieldMap = { glass_type: 'glass', frame: 'frame', sash: 'sash', hinge_type: 'hinge', net_type: 'net' };
    const origKey = fieldMap[field] || field;
    setNewItem(prev => ({ ...prev, [field]: prev._templateOriginal[origKey] || '' }));
  };

  const isCustomized = (field) => {
    if (!newItem._templateOriginal) return false;
    const fieldMap = { glass_type: 'glass', frame: 'frame', sash: 'sash', hinge_type: 'hinge', net_type: 'net' };
    const origKey = fieldMap[field] || field;
    return newItem[field] !== (newItem._templateOriginal[origKey] || '');
  };

  const selectCustomer = (custId) => {
    const c = customers.find(x => x.id === custId);
    if (c) setForm({ ...form, customer_id: c.id, customer_name: c.name, customer_phone: c.phone || '', customer_email: c.email || '', customer_address: c.address || '', delivery_address: c.site_address || c.address || '' });
  };

  const addItem = async () => {
    if (!newItem.vendor_id || !newItem.design_type || !newItem.width_mm || !newItem.height_mm) {
      toast.error('Vendor, Design, Width and Height are required');
      return;
    }
    try {
      const w = parseFloat(newItem.width_mm);
      const h = parseFloat(newItem.height_mm);
      if (w <= 0 || h <= 0) { toast.error('Width and Height must be greater than 0'); return; }

      const { data } = await api.post('/calculator/estimate', {
        vendor_id: newItem.vendor_id, design_type: newItem.design_type, width_mm: w, height_mm: h,
      });
      const vendor = vendors.find(v => v.id === newItem.vendor_id);
      const qty = parseInt(newItem.qty) || 1;
      const areaSqft = data.area_sqft || (data.area_sqm * SQM_TO_SQFT);
      // Use template rate_per_sqft if provided, else estimated
      const ratePerSqft = parseFloat(newItem.rate_per_sqft) || 0;
      const unitPrice = ratePerSqft > 0 ? Math.round(ratePerSqft * areaSqft) : data.estimated_unit_price;

      const handleParts = [];
      if (newItem.handle_inner && newItem.handle_inner !== 'None') handleParts.push(`Inner: ${newItem.handle_inner}`);
      if (newItem.handle_outer && newItem.handle_outer !== 'None') handleParts.push(`Outer: ${newItem.handle_outer}`);
      if (newItem.handle_mesh && newItem.handle_mesh !== 'None') handleParts.push(`Mesh: ${newItem.handle_mesh}`);

      setItems([...items, {
        sno: items.length + 1, design_type: newItem.design_type,
        vendor_id: newItem.vendor_id, vendor_name: vendor?.company_name || data.vendor_name,
        width_mm: w, height_mm: h, qty, area_sqm: data.area_sqm, area_sqft: areaSqft,
        glass_type: newItem.glass_type || data.glass_type,
        unit_price: unitPrice, total_price: Math.round(unitPrice * qty),
        color: newItem.color || 'White',
        net_type: newItem.net_type === 'None' ? null : newItem.net_type,
        handle_type: handleParts.join(' | ') || null,
        hinge_type: newItem.hinge_type === 'Not Applicable' ? null : newItem.hinge_type,
        frame: newItem.frame || '', sash: newItem.sash || '',
        series: newItem.series || vendor?.series_name || '',
        template_code: newItem.template_code || null,
      }]);
      setNewItem({
        vendor_id: newItem.vendor_id, design_type: '', width_mm: '', height_mm: '', qty: 1,
        glass_type: '', color: 'White', net_type: 'None', handle_inner: '', handle_outer: '', handle_mesh: '',
        hinge_type: '', frame: '', sash: '', series: '', rate_per_sqft: '',
        template_code: null, _templateOriginal: null,
      });
      setShowDetails(false);
      toast.success('Item added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not estimate rate');
    }
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, sno: i + 1 })));

  const updateItemField = (idx, field, value) => {
    const updated = [...items];
    updated[idx][field] = value;
    if (field === 'qty' || field === 'unit_price') {
      const q = parseInt(updated[idx].qty) || 1;
      const p = parseFloat(updated[idx].unit_price) || 0;
      if (q > 0) updated[idx].total_price = Math.round(p * q);
    }
    setItems(updated);
  };

  const subtotal = items.reduce((s, i) => s + (i.total_price || 0), 0);
  const transport = parseFloat(form.transport_charges) || 0;
  const installation = parseFloat(form.installation_charges) || 0;
  const gstAmount = Math.round(subtotal * (parseFloat(form.gst_rate) || 18) / 100);
  const grandTotal = subtotal + transport + installation + gstAmount;

  const save = async () => {
    if (!form.customer_name) { toast.error('Customer name is required'); return; }
    if (items.length === 0) { toast.error('Add at least one item'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        gst_rate: parseFloat(form.gst_rate) || 18,
        transport_charges: transport,
        installation_charges: installation,
        items,
        ...(isEdit ? { status } : {}),
      };
      if (isEdit) {
        await api.put(`/quotations/${editId}`, payload);
        toast.success('Quotation updated');
        navigate(`/quotations/${editId}`);
      } else {
        const { data } = await api.post('/quotations', payload);
        toast.success('Quotation saved successfully');
        navigate(`/quotations/${data.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save quotation');
    }
    setSaving(false);
  };

  const CustomBadge = ({ field }) => {
    if (!isCustomized(field)) return null;
    return (
      <span className="inline-flex items-center gap-1">
        <Badge variant="secondary" className="text-[9px] bg-amber-100 text-amber-700 px-1 py-0">Customized</Badge>
        <button type="button" onClick={() => resetField(field)} className="text-[9px] text-blue-500 hover:text-blue-700 underline">Reset</button>
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="create-quotation-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/quotations')} data-testid="back-btn"><ArrowLeft size={16} /></Button>
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">
            {isEdit ? `Edit Quotation ${quoteNumber}` : 'New Quotation'}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {isEdit ? 'Update items, prices, specifications or charges' : 'Create a professional quotation'}
          </p>
        </div>
      </div>

      {loadingExisting && (
        <div className="text-center py-12 text-slate-400 text-sm">Loading quotation…</div>
      )}

      {/* Customer */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-base font-['Outfit']">Customer Details</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Select Existing Customer</Label>
            <Select value={form.customer_id} onValueChange={selectCustomer}>
              <SelectTrigger data-testid="select-customer"><SelectValue placeholder="Select or enter manually" /></SelectTrigger>
              <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.phone ? `(${c.phone})` : ''}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} data-testid="cust-name-input" /></div>
            <div><Label>Phone</Label><Input value={form.customer_phone} onChange={e => setForm({...form, customer_phone: e.target.value})} data-testid="cust-phone-input" /></div>
            <div><Label>Email</Label><Input type="email" value={form.customer_email} onChange={e => setForm({...form, customer_email: e.target.value})} /></div>
            <div><Label>Address</Label><Input value={form.customer_address} onChange={e => setForm({...form, customer_address: e.target.value})} /></div>
          </div>
          <div><Label>Delivery / Site Address</Label><Input value={form.delivery_address} onChange={e => setForm({...form, delivery_address: e.target.value})} /></div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-base font-['Outfit']">Line Items</CardTitle></CardHeader>
        <CardContent>
          {/* Template selector */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <Label className="text-xs text-amber-700 font-semibold">Choose Product Template (prefills specs, W&H stay empty)</Label>
            <Select value="" onValueChange={code => { const t = templates.find(x => x.code === code); if (t) applyTemplate(t); }}>
              <SelectTrigger className="mt-1 h-9 text-xs bg-white" data-testid="template-select"><SelectValue placeholder="Select a template..." /></SelectTrigger>
              <SelectContent>
                {templates.map(t => <SelectItem key={t.code} value={t.code}>{t.code} — {t.name} ({formatINR(t.rate_per_sqft)}/sqft)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Add item form */}
          <div className="bg-slate-50 rounded-lg p-4 mb-4" data-testid="new-item-form">
            <div className="grid grid-cols-2 sm:grid-cols-7 gap-2 items-end">
              <div>
                <Label className="text-xs">Vendor</Label>
                <Select value={newItem.vendor_id} onValueChange={v => setNewItem({...newItem, vendor_id: v})}>
                  <SelectTrigger className="h-9 text-xs" data-testid="item-vendor-select"><SelectValue placeholder="Vendor" /></SelectTrigger>
                  <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Design Type {newItem.template_code && <Badge variant="secondary" className="text-[8px] ml-1 bg-blue-100 text-blue-700">{newItem.template_code}</Badge>}</Label>
                <Input className="h-9 text-xs" value={newItem.design_type} onChange={e => setNewItem({...newItem, design_type: e.target.value})} placeholder="Design type" data-testid="item-design-input" />
              </div>
              <div><Label className="text-xs">Width (mm) *</Label><Input type="number" className="h-9 text-xs" value={newItem.width_mm} onChange={e => setNewItem({...newItem, width_mm: e.target.value})} placeholder="Required" data-testid="item-width-input" /></div>
              <div><Label className="text-xs">Height (mm) *</Label><Input type="number" className="h-9 text-xs" value={newItem.height_mm} onChange={e => setNewItem({...newItem, height_mm: e.target.value})} placeholder="Required" data-testid="item-height-input" /></div>
              <div><Label className="text-xs">Qty</Label><Input type="number" className="h-9 text-xs" value={newItem.qty} onChange={e => setNewItem({...newItem, qty: e.target.value})} min="1" /></div>
              <div><Label className="text-xs">Rate/sqft</Label><Input type="number" className="h-9 text-xs" value={newItem.rate_per_sqft} onChange={e => setNewItem({...newItem, rate_per_sqft: e.target.value})} placeholder="₹" /></div>
              <div className="flex gap-1">
                <Button onClick={() => setShowDetails(!showDetails)} variant="outline" className="h-9 text-xs px-2" data-testid="toggle-details-btn">
                  {showDetails ? 'Hide' : 'Specs'}
                </Button>
                <Button onClick={addItem} className="h-9 flex-1 bg-[#1a365d] hover:bg-[#102a4c] text-white text-xs" data-testid="add-item-btn">
                  <Plus size={14} />
                </Button>
              </div>
            </div>

            {showDetails && (
              <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div><Label className="text-xs text-slate-500">Color</Label><Input className="h-8 text-xs" value={newItem.color} onChange={e => setNewItem({...newItem, color: e.target.value})} /></div>
                <div>
                  <Label className="text-xs text-slate-500">Net / Mesh <CustomBadge field="net_type" /></Label>
                  <Select value={newItem.net_type} onValueChange={v => setNewItem({...newItem, net_type: v})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{MESH_OPTIONS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Glass <CustomBadge field="glass_type" /></Label>
                  <Input className="h-8 text-xs" value={newItem.glass_type} onChange={e => setNewItem({...newItem, glass_type: e.target.value})} placeholder="5mm Clear" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Handle - Inner Track</Label>
                  <Select value={newItem.handle_inner} onValueChange={v => setNewItem({...newItem, handle_inner: v})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{HANDLE_CHOICES.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Handle - Outer Track</Label>
                  <Select value={newItem.handle_outer} onValueChange={v => setNewItem({...newItem, handle_outer: v})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{HANDLE_CHOICES.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Handle - Mesh Track</Label>
                  <Select value={newItem.handle_mesh} onValueChange={v => setNewItem({...newItem, handle_mesh: v})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{HANDLE_CHOICES.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Hinge</Label>
                  <Input className="h-8 text-xs" value={newItem.hinge_type} onChange={e => setNewItem({...newItem, hinge_type: e.target.value})} placeholder="Flat Hinge" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Frame <CustomBadge field="frame" /></Label>
                  <Input className="h-8 text-xs" value={newItem.frame} onChange={e => setNewItem({...newItem, frame: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Sash <CustomBadge field="sash" /></Label>
                  <Input className="h-8 text-xs" value={newItem.sash} onChange={e => setNewItem({...newItem, sash: e.target.value})} />
                </div>
                <div><Label className="text-xs text-slate-500">Series</Label><Input className="h-8 text-xs" value={newItem.series} onChange={e => setNewItem({...newItem, series: e.target.value})} /></div>
              </div>
            )}

            {/* Live Technical Drawing Preview */}
            {newItem.design_type && newItem.width_mm && newItem.height_mm && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <DrawingPreview item={newItem} />
              </div>
            )}
          </div>

          {/* Items table */}
          {items.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm" data-testid="line-items-table">
                <thead>
                  <tr className="bg-[#1a365d] text-white">
                    <th className="text-left px-3 py-2 text-xs font-medium">#</th>
                    <th className="text-left px-3 py-2 text-xs font-medium">Design</th>
                    <th className="text-left px-3 py-2 text-xs font-medium">Specs</th>
                    <th className="text-right px-3 py-2 text-xs font-medium">W(mm)</th>
                    <th className="text-right px-3 py-2 text-xs font-medium">H(mm)</th>
                    <th className="text-right px-3 py-2 text-xs font-medium">Qty</th>
                    <th className="text-right px-3 py-2 text-xs font-medium">Sqft</th>
                    <th className="text-right px-3 py-2 text-xs font-medium">Rate</th>
                    <th className="text-right px-3 py-2 text-xs font-medium">Amount</th>
                    <th className="px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="px-3 py-2 text-xs">{idx + 1}</td>
                      <td className="px-3 py-2 text-xs font-medium text-[#1a365d]">
                        {item.design_type}
                        {item.template_code && <Badge variant="secondary" className="ml-1 text-[8px] bg-blue-50 text-blue-600">{item.template_code}</Badge>}
                      </td>
                      <td className="px-3 py-2 text-[10px] text-slate-500 leading-tight max-w-[180px]">
                        {[item.glass_type && `Glass: ${item.glass_type}`, item.net_type && `Mesh: ${item.net_type}`, item.handle_type && `Handle: ${item.handle_type}`, item.color && `Color: ${item.color}`].filter(Boolean).join(' | ')}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{item.width_mm}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{item.height_mm}</td>
                      <td className="px-3 py-2 text-right"><Input type="number" className="h-7 w-14 text-xs text-right p-1 tabular-nums" value={item.qty} min="1" onChange={e => updateItemField(idx, 'qty', parseInt(e.target.value) || 1)} /></td>
                      <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">{item.area_sqft?.toFixed(1)}</td>
                      <td className="px-3 py-2 text-right"><Input type="number" className="h-7 w-24 text-xs text-right p-1 font-mono tabular-nums" value={item.unit_price} onChange={e => updateItemField(idx, 'unit_price', parseFloat(e.target.value) || 0)} /></td>
                      <td className="px-3 py-2 text-right font-mono text-xs font-medium tabular-nums">{formatINR(item.total_price)}</td>
                      <td className="px-2 py-2"><button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 text-slate-400 text-sm">
              <FileText size={36} className="mx-auto mb-2 opacity-30" />
              <p>No items yet. Select a template or add items manually above.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings + Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-slate-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-['Outfit']">Settings</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>GST Type</Label>
                <Select value={form.gst_type} onValueChange={v => setForm({...form, gst_type: v})}>
                  <SelectTrigger data-testid="gst-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CGST_SGST">CGST + SGST</SelectItem><SelectItem value="IGST">IGST</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>GST Rate (%)</Label><Input type="number" value={form.gst_rate} onChange={e => setForm({...form, gst_rate: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Transport (₹)</Label><Input type="number" value={form.transport_charges} onChange={e => setForm({...form, transport_charges: e.target.value})} data-testid="transport-input" /></div>
              <div><Label>Installation (₹)</Label><Input type="number" value={form.installation_charges} onChange={e => setForm({...form, installation_charges: e.target.value})} data-testid="installation-input" /><span className="text-[10px] text-slate-400">₹30/sqft, min ₹1,800</span></div>
            </div>
            <div><Label>Terms & Conditions</Label><Textarea value={form.terms} onChange={e => setForm({...form, terms: e.target.value})} rows={4} className="text-xs" /></div>
          </CardContent>
        </Card>

        <Card className="border-2 border-[#1a365d]">
          <CardHeader className="pb-3 bg-[#1a365d] text-white rounded-t-lg"><CardTitle className="text-base font-['Outfit']">Quotation Summary</CardTitle></CardHeader>
          <CardContent className="pt-4 space-y-3">
            <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-mono font-medium tabular-nums">{formatINR(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Transport</span><span className="font-mono tabular-nums">{formatINR(transport)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Installation</span><span className="font-mono tabular-nums">{formatINR(installation)}</span></div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{form.gst_type === 'CGST_SGST' ? 'CGST + SGST' : 'IGST'} ({form.gst_rate}%)</span>
              <span className="font-mono tabular-nums">{formatINR(gstAmount)}</span>
            </div>
            <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
              <span className="font-['Outfit'] font-semibold text-lg text-[#1a365d]">Grand Total</span>
              <span className="font-['Outfit'] font-semibold text-2xl text-[#1a365d] tabular-nums" data-testid="grand-total">{formatINR(grandTotal)}</span>
            </div>
            <Button onClick={save} className="w-full mt-4 bg-[#ed8936] hover:bg-[#dd6b20] text-white font-medium py-3" disabled={saving} data-testid="save-quotation-btn">
              {saving ? (isEdit ? 'Updating...' : 'Saving...') : (isEdit ? 'Update Quotation' : 'Save Quotation')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
